import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'lib/utils';

const badgeVariants = cva(
  // Base — mono uppercase, sharp edges (the only place pills exist is
  // status badges, opted into via variant). Tabular nums for counters.
  'inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.12em] [&_.dot]:h-1.5 [&_.dot]:w-1.5 [&_.dot]:rounded-full [&_.dot]:bg-current',
  {
    variants: {
      variant: {
        default: 'border-[var(--line)] bg-[var(--surface-2)] text-[var(--text)]',
        // ===== Status — paired with a small <span class="dot" /> =====
        success:
          'border-[rgba(0,187,127,0.25)] bg-[rgba(0,187,127,0.12)] text-[var(--rye-green)] dark:border-[rgba(0,187,127,0.25)]',
        warn: 'border-[rgba(249,156,0,0.3)] bg-[rgba(249,156,0,0.14)] text-[var(--rye-amber)]',
        error: 'border-[rgba(255,101,104,0.3)] bg-[rgba(255,101,104,0.14)] text-[var(--rye-red)]',
        info: 'border-[rgba(20,71,230,0.32)] bg-[rgba(20,71,230,0.16)] text-[#1447e6] dark:text-[#6585ee]',
        violet: 'border-[rgba(172,75,255,0.3)] bg-[rgba(172,75,255,0.14)] text-[var(--rye-violet)]',
        neutral: 'border-[var(--line)] bg-[var(--surface-2)] text-[var(--text-muted)]',
        brand: 'border-[var(--accent)] bg-[var(--accent)] text-[var(--accent-fg)]',
        // shadcn legacy aliases — keep so we don't break callsites.
        secondary: 'border-[var(--line)] bg-[var(--surface-2)] text-[var(--text-muted)]',
        destructive:
          'border-[rgba(255,101,104,0.3)] bg-[rgba(255,101,104,0.14)] text-[var(--rye-red)]',
        outline: 'border-[var(--line-strong)] bg-transparent text-[var(--text)]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
